/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApplyEditsRequest } from '../models/ApplyEditsRequest';
import type { ApplyEditsResponse } from '../models/ApplyEditsResponse';
import type { CloseProgramSchema } from '../models/CloseProgramSchema';
import type { CreateProgramRequest } from '../models/CreateProgramRequest';
import type { ProgramDetailResponse } from '../models/ProgramDetailResponse';
import type { ProgramListResponse } from '../models/ProgramListResponse';
import type { TransferProgramRequest } from '../models/TransferProgramRequest';
import type { UpdateProgramRequest } from '../models/UpdateProgramRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ProgramsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Apply AI-generated edits to a program
     * @returns ApplyEditsResponse Edits applied
     * @throws ApiError
     */
    public postProgramsApplyEdits({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: ApplyEditsRequest,
    }): CancelablePromise<ApplyEditsResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/apply-edits',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request (invalid URL)`,
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

    /**
     * Delete a program
     * @returns any Program deleted
     * @throws ApiError
     */
    public deletePrograms({
        id,
    }: {
        id: string,
    }): CancelablePromise<{
        message: string;
    }> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/programs/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

    /**
     * Get program details with ordered videos
     * @returns ProgramDetailResponse Program details
     * @throws ApiError
     */
    public getProgram({
        id,
    }: {
        id: string,
    }): CancelablePromise<ProgramDetailResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/programs/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

    /**
     * Update program details or video ordering
     * @returns ProgramDetailResponse Program updated
     * @throws ApiError
     */
    public patchPrograms({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: UpdateProgramRequest,
    }): CancelablePromise<ProgramDetailResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/programs/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

    /**
     * Clone a program for a client
     * Duplicates a program and assigns it to a client. Only trainers can clone to a customer.
     * @returns any Program cloned successfully
     * @throws ApiError
     */
    public postProgramsClone({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: {
            clientId: string;
            copyOverrides?: boolean;
        },
    }): CancelablePromise<{
        programId: string;
        message: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/clone',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request`,
                403: `Forbidden - not a trainer or not linked`,
                404: `Program or customer not found`,
            },
        });
    }

    /**
     * Share a program with a customer (alias of /clone)
     * Duplicates a program and assigns it to a client. Only trainers can clone to a customer.
     * @returns any Program cloned successfully
     * @throws ApiError
     */
    public postProgramsShare({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: {
            clientId: string;
            copyOverrides?: boolean;
        },
    }): CancelablePromise<{
        programId: string;
        message: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/share',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request`,
                403: `Forbidden - not a trainer or not linked`,
                404: `Program or customer not found`,
            },
        });
    }

    /**
     * Assign a program to a client without copying it
     * Sets clientId on the program in place; ownerId (coaching authority) is unchanged. Trainer-only, requires an existing trainer-customer link.
     * @returns any Program assigned successfully
     * @throws ApiError
     */
    public postProgramsAssign({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: {
            clientId: string;
        },
    }): CancelablePromise<{
        programId: string;
        message: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/assign',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request`,
                403: `Forbidden - not a trainer, not owner, or not linked`,
                404: `Program or customer not found`,
            },
        });
    }

    /**
     * List programs for current user or customer
     * @returns ProgramListResponse List of programs
     * @throws ApiError
     */
    public getPrograms({
        customerId,
        includeArchived,
    }: {
        customerId?: string,
        includeArchived?: boolean | null,
    }): CancelablePromise<ProgramListResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/programs',
            query: {
                'customerId': customerId,
                'includeArchived': includeArchived,
            },
            errors: {
                403: `Forbidden`,
            },
        });
    }

    /**
     * Create a new program from clips or bookmarks
     * @returns ProgramDetailResponse Program created
     * @throws ApiError
     */
    public postPrograms({
        requestBody,
    }: {
        requestBody: CreateProgramRequest,
    }): CancelablePromise<ProgramDetailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden`,
                404: `Not found`,
            },
        });
    }

    /**
     * Close out a program and document final target outcomes
     * @returns ProgramDetailResponse Program successfully closed
     * @throws ApiError
     */
    public postProgramsClose({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: CloseProgramSchema,
    }): CancelablePromise<ProgramDetailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/close',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error`,
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

    /**
     * Transfer coaching authority of a program to a new owner
     * @returns ProgramDetailResponse Program successfully transferred
     * @throws ApiError
     */
    public postProgramsTransfer({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: TransferProgramRequest,
    }): CancelablePromise<ProgramDetailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/programs/{id}/transfer',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error`,
                403: `Forbidden`,
                404: `Program not found`,
            },
        });
    }

}
